'use strict';

// Preserve native prescription fields and progress; configure immutable priced combinations.
window.ERLensColours = (() => {
  const data = window.ER_LENS_COLOURS;
  // Customer-approved guidance; pricing and available combinations stay in the catalog.
  const guidance = {
    pc: {
      benefit: 'Lightweight and impact-resistant.',
      sport: 'For running, cycling and active days.',
      compact: 'Light, impact-resistant lenses for running, cycling and active days.',
      detail: 'Lightweight and impact-resistant for active days. Higher-index options may be thinner for stronger prescriptions.'
    },
    mr8: {
      benefit: 'Everyday clarity in a thinner lens.',
      sport: 'For golf, hiking and daily wear.',
      compact: 'Everyday clarity with a thinner profile for golf, hiking and daily wear.',
      detail: 'A balanced everyday choice. A 1.60 lens can be thinner than standard plastic for the same prescription.'
    },
    mr7: {
      benefit: 'Less bulky edges for stronger prescriptions.',
      sport: 'Consider when lens thickness matters most.',
      note: 'Thickness also depends on your frame.',
      compact: 'Helps reduce bulky lens edges in stronger prescriptions.',
      detail: 'The highest index here. Final thickness also depends on your prescription and frame; a higher index does not automatically mean a lighter lens.'
    },
    trivex: {
      benefit: 'Light weight, clear vision and impact resistance.',
      sport: 'For long runs, hikes and court sports.',
      note: 'A lower index may mean thicker lenses.',
      compact: 'Lightweight, clear and impact-resistant for runs, hikes and court sports.',
      detail: 'Combines light weight, optical clarity and impact resistance. Its lower index may mean thicker lenses for stronger prescriptions.'
    },
    photochromic: {
      benefit: 'Darkens in sunlight and fades lighter indoors.',
      sport: 'Best for: runs, rides and hikes through sun and shade.',
      note: 'The tint takes time to change.',
      compact: 'Darkens outdoors; fades lighter indoors. For routes through sun and shade.',
      detail: 'The tint takes time to change. Photochromic alone does not include polarization.'
    },
    polarized: {
      benefit: 'Cuts glare reflected from water and wet roads.',
      sport: 'Best for: fishing, waterside outings and reflective roads.',
      note: 'Fixed tint unless you also choose Photochromic.',
      compact: 'Cuts glare from water and wet roads. For fishing and reflective routes.',
      detail: 'Reduces reflected glare on bright water and wet roads. The tint stays fixed unless you also choose Photochromic.'
    },
    mirror: {
      benefit: 'Adds a coloured reflective finish to the lens surface.',
      sport: 'Best for: bright outdoor rides and runs.',
      note: 'Mirror colour is the outside reflection.',
      compact: 'Coloured outer reflections for bright outdoor rides and runs.',
      detail: 'Mirror colour describes the outside reflection, not the tint you look through. Polarized and Photochromic are separate options.'
    }
  };
  const optionsFor = pack => window.ERLensOptionsFor(pack);
  const resolve = (pack, id) => window.ERResolveLensColour(pack, id);
  const materialsFor = (packages, type) => window.ERLensMaterialsFor(type);
  const money = fee => window.ERMoney(fee);
  const make = (tag, cls = '', text) => {
    const element = document.createElement(tag);
    if (cls) element.className = cls;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  function choiceCard(input, title, fee, id, additional = false) {
    const advice = guidance[id];
    const card = make('div', 'er-lens-choice-card');card.dataset.erLensChoice = id;
    const label = make('label', 'er-lens-choice er-lens-choice-main');
    const copy = make('span', 'er-lens-option-copy');
    const heading = make('span', 'er-lens-option-heading');
    heading.append(make('strong', '', title), make('span', 'er-lens-option-price', (additional ? '+' : '') + money(fee).replace(/\.00$/, '')));
    const full = make('span', 'er-lens-option-full');
    full.append(make('small', '', advice.benefit), make('small', 'er-lens-option-sport', advice.sport));
    if (advice.note) full.append(make('small', 'er-lens-option-note', advice.note));
    copy.append(heading, full, make('small', 'er-lens-option-compact', advice.compact));
    label.append(input, copy);
    // Keep the disclosure outside the label: opening it must not select an option.
    const details = make('details', 'er-lens-option-details');
    const summary = make('summary', '', 'Details');summary.setAttribute('aria-label', title + ' details');
    details.append(summary, make('p', '', advice.detail));card.append(label, details);
    return card;
  }
  function photo(box, caption) {
    const wrap = make('span', 'er-lens-colour-photo');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', box.join(' '));
    svg.setAttribute('aria-hidden', 'true');
    svg.style.overflow = 'hidden';
    const clipId = 'er-colour-crop-' + box.join('-');
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    clip.id = clipId;
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    ['x', 'y', 'width', 'height'].forEach((name, index) => rect.setAttribute(name, String(box[index])));
    clip.append(rect);defs.append(clip);
    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    image.setAttribute('href', data.referenceAsset);
    image.setAttribute('width', String(data.referenceWidth));
    image.setAttribute('height', String(data.referenceHeight));
    image.setAttribute('clip-path', 'url(#' + clipId + ')');
    svg.append(defs,image);wrap.append(svg);
    if (caption) wrap.append(make('small', '', caption));
    return wrap;
  }
  function priceBreakdown(pack) {
    const block = make('div', 'er-lens-breakdown');
    block.setAttribute('aria-label', 'Lens price per pair');
    for (const row of window.ERLensPriceRows(pack)) {
      const line = make('div');
      line.append(make('span', '', row.label), make('span', '', money(row.fee)));
      block.append(line);
    }
    const total = make('div', 'er-lens-subtotal');
    total.append(make('strong', '', 'Lens total / pair'), make('strong', '', money(pack.fee)));
    block.append(total);
    return block;
  }
  function mount(root, packages) {
    const colourMemory = new Map();
    let panel, panelKey = '', picker, pickerKey = '', breakdown, breakdownKey = '';
    let reviewBreakdown, reviewKey = '', scheduled = false, showError = false;
    const context = () => {
      const type = root.querySelector('[name="rx_preview_type"]:checked')?.value || 'single';
      const native = root.querySelector('[name="rx_preview_lens"]:checked');
      const pack = packages.find(item => item.id === native?.value);
      return {type, pack: pack?.type === (type === 'none' ? 'single' : type) ? pack : null};
    };
    const selection = () => {
      const pack = context().pack;
      if (!pack) return null;
      const options = optionsFor(pack);
      return resolve(pack, colourMemory.get(pack.id)) || (options.length === 1 ? options[0] : null);
    };
    // Dispatch the existing native package event; prices remain native, never text-only overrides.
    function selectPackage(id) {
      const inputs = [...root.querySelectorAll('[name="rx_preview_lens"]')];
      const input = inputs.find(item => item.value === id) || inputs.find(item => item.checked) || inputs[0];
      if (!input) return;
      inputs.forEach(item => { item.checked = false; });
      const previous = input.value;
      input.value = id;input.checked = !!id;
      input.dispatchEvent(new Event('change', {bubbles: true}));
      input.value = previous;
    }
    function chooseMaterial(input) {
      const current = context();
      if (current.pack?.materialId !== input.value)
        selectPackage(window.ERLensPackageId(current.type, input.value, 0));
      showError = false;schedule();
    }
    function chooseFeature() {
      const current = context();
      if (!current.pack) return;
      const colour = selection();
      const mask = [...panel.querySelectorAll('[name="er_preview_feature"]:checked')]
        .reduce((value, input) => value | Number(input.value), 0);
      const id = window.ERLensPackageId(current.type, current.pack.materialId, mask);
      const next = packages.find(pack => pack.id === id);
      if (next && resolve(next, colour?.id)) colourMemory.set(id, colour.id);
      if (id !== current.pack.id) selectPackage(id);
      showError = false;schedule();
    }
    function renderControls(current) {
      const catalog = root.querySelector('[data-rx-catalog]');
      if (!catalog?.querySelector('[name="rx_preview_lens"]')) return;
      if (!catalog.hidden) catalog.hidden = true;
      if (panelKey !== current.type || !panel?.isConnected) {
        panel?.remove();panelKey = current.type;pickerKey = '';breakdownKey = '';
        panel = make('div', 'er-lens-configurator');
        panel.dataset.erLensConfigurator = '';
        const materialSection = make('fieldset', 'er-lens-materials');
        const legend = make('legend', '', '1. Choose your material');legend.tabIndex = -1;
        materialSection.append(legend, make('p', 'er-lens-colour-intro', 'Clear lens prices per pair. Eyewear is priced separately.'));
        const materialGrid = make('div', 'er-lens-material-grid');
        for (const item of materialsFor(packages, current.type)) {
          const input = make('input');input.type = 'radio';input.name = 'er_preview_material';input.value = item.id;
          input.setAttribute('aria-label', item.material);
          materialGrid.append(choiceCard(input, item.material, item.baseFee, item.id));
        }
        materialSection.append(materialGrid);panel.append(materialSection);
        const featureSection = make('fieldset', 'er-lens-finishes');
        const featureLegend = make('legend', '', '2. Add what suits your day');featureLegend.tabIndex = -1;
        featureSection.append(featureLegend, make('p', 'er-lens-colour-intro', window.ERTheme.currency==='USD'?`Each selected feature adds ${window.ERMoney(window.ER_LENS_PRICING.featureFee)} per pair. None selected = Clear.`:'Choose any combination. Your lens total updates below. None selected = Clear.'));
        const featureGrid = make('div', 'er-lens-finish-grid');
        for (const feature of window.ER_LENS_PRICING.features) {
          const input = make('input');input.type = 'checkbox';input.name = 'er_preview_feature';input.value = String(feature.bit);
          input.setAttribute('aria-label', feature.label);
          featureGrid.append(choiceCard(input, feature.label, window.ERFeaturePrice(current.pack,feature.bit), feature.id, true));
        }
        featureSection.append(featureGrid);panel.append(featureSection);
        catalog.before(panel);
      }
      panel.querySelectorAll('[name="er_preview_material"]').forEach(input => { input.checked = input.value === current.pack?.materialId; });
      panel.querySelectorAll('[name="er_preview_feature"]').forEach(input => {
        input.disabled = !current.pack;
        input.checked = !!(current.pack?.featureMask & Number(input.value));
      });
      if (current.pack?.id !== breakdownKey || !breakdown?.isConnected) {
        breakdown?.remove();breakdownKey = current.pack?.id || '';
        if (current.pack) {breakdown = priceBreakdown(current.pack);panel.append(breakdown);}
      }
    }
    function renderPicker(current) {
      const key = current.pack?.id || '';
      if (pickerKey === key && (picker?.isConnected || !current.pack)) return;
      picker?.remove();picker = null;pickerKey = key;
      if (!current.pack) return;
      const options = optionsFor(current.pack);
      const mirror = current.pack.features.includes('mirror');
      picker = make('fieldset', 'er-lens-colour-picker');
      picker.dataset.erLensColours = current.pack.id;
      const legend = make('legend', '', mirror ? '3. Mirror colour' : '3. Lens colour');legend.tabIndex = -1;picker.append(legend);
      if (options.length === 1 && options[0].id === 'clear') {
        picker.append(make('p', 'er-fixed-colour', 'Clear · No optional features selected'));
      } else {
        picker.append(make('p', 'er-lens-colour-intro', mirror ? 'Choose your mirror colour.' : 'Choose your lens colour.'));
        const grid = make('div', 'er-lens-colour-grid');
        for (const option of options) {
          const label = make('label', 'er-lens-colour-option');
          const input = make('input');input.type = 'radio';input.name = 'er_preview_lens_colour';input.value = option.id;
          input.setAttribute('aria-label', option.label);
          const visual = make('span', 'er-lens-colour-visual');
          if (option.before && option.after) visual.append(photo(option.before, 'Before'), photo(option.after, 'After'));
          else if (option.image) visual.append(photo(option.image));
          else if (option.swatch) {
            const swatch = make('span', 'er-lens-colour-swatch');
            swatch.style.background = option.swatch;
            swatch.setAttribute('aria-hidden', 'true');visual.append(swatch);
          }
          label.append(input, visual, make('strong', '', option.label));grid.append(label);
        }
        picker.append(grid, make('p', 'er-lens-colour-reference-note', 'Colour references only. Actual shade and reflections vary with light.'));
      }
      if (breakdown?.isConnected) breakdown.before(picker);else panel.append(picker);
    }
    function updateReview(current) {
      const description = root.querySelector('[data-rx-review-description]');
      const name = root.querySelector('[data-rx-review-lens]');
      let row = root.querySelector('[data-er-review-lens-colour]');
      if (!description || !current.pack) {row?.remove();reviewBreakdown?.remove();reviewKey = '';return;}
      const displayName = current.type === 'none' ? current.pack.name.replace('Single vision', 'Non-prescription') : current.pack.name;
      if (name.textContent !== displayName) name.textContent = displayName;
      if (description.textContent !== current.pack.description) description.textContent = current.pack.description;
      for (const term of root.querySelectorAll('[data-rx-review-prescription] dt')) {
        if (term.textContent.trim() !== 'Lens Group') continue;
        const value = term.nextElementSibling;
        if (value && value.textContent !== current.pack.description) value.textContent = current.pack.description;
      }
      if (!row) {row = make('p', 'er-review-lens-colour');row.dataset.erReviewLensColour = '';description.after(row);}
      const colour = selection();
      const text = 'Lens colour: ' + (colour?.label || 'Choose a lens colour');
      if (row.textContent !== text) row.textContent = text;
      if (reviewKey !== current.pack.id || !reviewBreakdown?.isConnected) {
        reviewBreakdown?.remove();reviewBreakdown = priceBreakdown(current.pack);reviewKey = current.pack.id;
        row.after(reviewBreakdown);
      }
    }
    let disposed=false;
    function update() {
      if(disposed)return;
      const current = context();renderControls(current);renderPicker(current);updateReview(current);
      if (!panel) return;
      const chosen = selection();
      picker?.querySelectorAll('[name="er_preview_lens_colour"]').forEach(input => { input.checked = input.value === chosen?.id; });
      let error = panel.querySelector('.er-lens-config-error');
      if (!error) {error = make('p', 'er-lens-config-error');error.setAttribute('role', 'alert');panel.append(error);}
      const message = !current.pack ? 'Choose a material and index to continue.' : !chosen ? `Choose a ${current.pack.features.includes('mirror') ? 'mirror' : 'lens'} colour to continue.` : '';
      if (error.textContent !== message) error.textContent = message;
      const hidden = !showError || !message;if (error.hidden !== hidden) error.hidden = hidden;
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;queueMicrotask(() => {scheduled = false;update();});
    }
    const customInput = event => {
      const input = event.target;
      if (input.name === 'er_preview_material') chooseMaterial(input);
      else if (input.name === 'er_preview_feature') chooseFeature();
      else if (input.name === 'er_preview_lens_colour') {
        const pack = context().pack;
        if (resolve(pack, input.value)) colourMemory.set(pack.id, input.value);
        showError = false;
      }
      schedule();
    };
    // Native input handling changes visibility before change; commit custom choices first.
    root.addEventListener('input', customInput, true);
    root.addEventListener('change', customInput);
    // Native click handlers queue visibility updates before input/change dispatch.
    root.addEventListener('click', event => {
      if (event.target.matches('input[name^="er_preview_"]')) customInput(event);
    }, true);
    root.addEventListener('change', event => {
      if (event.target.name === 'rx_preview_type') {selectPackage('');showError = false;schedule();}
    }, true);
    root.addEventListener('click', event => {
      if (!event.target.closest('[data-rx-next]')) return;
      const step = root.querySelector('[data-rx-step="1"]'),review = root.querySelector('[data-rx-step="2"]');
      if ((step.hidden && review.hidden) || selection()) return;
      event.preventDefault();event.stopImmediatePropagation();showError = true;update();
      const target = context().pack ? picker : panel?.querySelector('.er-lens-materials');
      target?.querySelector('legend')?.focus();target?.scrollIntoView({block: 'nearest'});
    }, true);
    const observer=new MutationObserver(schedule);observer.observe(root, {subtree: true, childList: true, attributes: true, attributeFilter: ['hidden']});
    schedule();
    return {selection, resolve, optionsFor,destroy(){disposed=true;observer.disconnect();},suspend(){disposed=true;observer.disconnect();},resume(){if(!root.isConnected)return;disposed=false;observer.observe(root,{subtree:true,childList:true,attributes:true,attributeFilter:['hidden']});schedule();}};
  }
  return {mount, resolve, optionsFor, materialsFor};
})();
